import { AuthService } from './authService';
import { logger } from './loggingService';

/**
 * Enhanced API wrapper that automatically logs all API calls
 */
export class ApiClient {
  private static async makeRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const startTime = Date.now();
    const method = options.method || 'GET';
    
    try {
      // Log API call start
      logger.debug('API', `Starting ${method} request to ${url}`);
      
      const response = await AuthService.authenticatedFetch(url, options);
      const duration = Date.now() - startTime;
      
      // Log successful API call
      logger.logApiCall(method, url, response.status, duration);
      
      return response;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Log failed API call
      logger.logApiCall(method, url, undefined, duration, error as Error);
      
      throw error;
    }
  }

  static async get(url: string): Promise<Response> {
    return this.makeRequest(url, { method: 'GET' });
  }

  static async post(url: string, data?: any): Promise<Response> {
    return this.makeRequest(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  static async put(url: string, data?: any): Promise<Response> {
    return this.makeRequest(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  static async patch(url: string, data?: any): Promise<Response> {
    return this.makeRequest(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  static async delete(url: string): Promise<Response> {
    return this.makeRequest(url, { method: 'DELETE' });
  }
}

/**
 * Performance monitoring decorator
 */
export function logPerformance(category: string = 'Performance') {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    
    descriptor.value = async function (...args: any[]) {
      const startTime = Date.now();
      const methodName = `${target.constructor.name}.${propertyKey}`;
      
      try {
        const result = await originalMethod.apply(this, args);
        const duration = Date.now() - startTime;
        
        logger.logPerformance(methodName, duration, { category, args: args.length });
        
        return result;
      } catch (error) {
        const duration = Date.now() - startTime;
        logger.logPerformance(methodName, duration, { category, args: args.length, failed: true });
        throw error;
      }
    };
    
    return descriptor;
  };
}

/**
 * Automatic error logging decorator
 */
export function logErrors(category: string) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    
    descriptor.value = async function (...args: any[]) {
      try {
        return await originalMethod.apply(this, args);
      } catch (error) {
        const methodName = `${target.constructor.name}.${propertyKey}`;
        logger.logException(category, error as Error, { 
          method: methodName,
          args: args.length 
        });
        throw error;
      }
    };
    
    return descriptor;
  };
}
